import React, { forwardRef, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import _ from "lodash";
import GenericMappingForm from '../GenericMappingForm';

const WhiteHatSentinelMappingForm = forwardRef(function WhiteHatSentinelMappingForm({disableProjectFields = false, ...props}, ref) {
  const [initialValues] = useState(() => {
    if (props.record) {
      return undefined;
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });
  
  const [mappedProjectVerionList] = useState(() => {
    // this state contains the already mapped application/site
    // This is stored because the project and version dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { applicationId, applicationName, siteId, siteName } = props.record;
      return {
        application: applicationId ? [{ id: applicationId, name: applicationName }] : [],
        site: siteId ? [{ id: siteId, name: siteName }] : []
      }
    }
    return { application: [], site: [] }
  });
  
  const projectRef = useRef({ application: mappedProjectVerionList.application, site:mappedProjectVerionList.site });

  const fetchProjects = useCallback((key, params) =>{
    // eslint-disable-next-line prefer-const
      let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,type:key, ...params });
      promise = promise.then((response) => {
        // merging the already mapped application/site to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectVerionList[key].length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectVerionList[key]];
        }
        projectRef.current[key] = _.uniqBy([...projectRef.current[key], ...response.projects], 'id');
        return response;
      });
      return { promise, cancel };
  },[props.loginConfigId,props.toolName, mappedProjectVerionList])

  const fetchApplication = useCallback((params) => {
      return fetchProjects("application", params)
  }, [fetchProjects]);

  const fetchSites = useCallback((params) => {
    return fetchProjects("site", params)
}, [fetchProjects]);

  const getExtraFieldsData = useCallback((config) => {
    const { applicationId, siteId } = config;
    if (_.isEmpty(applicationId) && _.isEmpty(siteId)) {
      throw new Error("Please select atleast one Application/Site");
    }

    const applicationName = _.find(projectRef.current.application, { id: applicationId })?.name, siteName = _.find(projectRef.current.site, { id: siteId })?.name;
    return ({
      applicationId, applicationName, siteId, siteName,
    });
  }, []);

  const extraFields = useMemo(() => ([
    {
      name: 'applicationId',
      colSpan: '12',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Application Name',
      extra: 'Provide at least one: Application or Site.',
      disabled: disableProjectFields,
      props: {
          api: fetchApplication,
          allowClear: true,
          autoFocus: !disableProjectFields,
          setFormListItemProps: (_, listField) => ({
            dependencies: [['configurations', listField.name, 'siteId']],
            rules: [
              ({ getFieldValue }) => ({
                validator(_, appVal) {
                  const siteVal = getFieldValue(['configurations', listField.name, 'siteId']);
                  if (appVal || siteVal) return Promise.resolve();
                  return Promise.reject(new Error('Enter Application or Site'));
                },
              }),
            ],
          }),
      }
    },{
      name: 'siteId',
      colSpan: '12',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      label: 'Site Name',
      props: {
        api: fetchSites,
        allowClear: true
    }
    }
  ]), [fetchApplication, fetchSites, disableProjectFields]);

  return (
    <GenericMappingForm
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      title="Map WhiteHat Sentinel Projects"
      formDefaultValues={initialValues}
      {...props}
    />
  )
})

export default WhiteHatSentinelMappingForm;
